import { createFileRoute } from "@tanstack/react-router";
import { pageHead, serviceSchema, breadcrumbSchema, jsonLdScript } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

const PAGE_PATH = "/en/services/geo-generative-engine-optimization";

const HERO_DESCRIPTION =
  "Generative Engine Optimization makes sure you're among the sources ChatGPT, Perplexity and Google's AI Overviews cite in their answers — not everywhere, but where the mention actually pays off commercially.";

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "What is Generative Engine Optimization (GEO)?",
    a: "GEO is the systematic optimisation of content and digital presence so that AI systems such as ChatGPT, Perplexity and Google's AI Overviews cite or recommend a brand as a source. Unlike classic SEO, GEO doesn't target a ranking position but a mention in the synthesised answer.",
  },
  {
    q: "How does GEO differ from classic SEO?",
    a: "SEO gets you into the results list, GEO into the AI answer. SEO optimises for rankings, GEO for selection as a cited source. Both share the technical foundation — clean structure, structured data, authority — but they're measured differently.",
  },
  {
    q: "Can a placement in AI answers be guaranteed?",
    a: "No. Which sources an AI system names changes from prompt to prompt and month to month. Serious work raises the probability of a mention and makes it measurable — it cannot guarantee one.",
  },
  {
    q: "Which companies is GEO worth it for?",
    a: "Above all where audiences prepare purchase or engagement decisions through AI assistants — typically explanation-heavy B2B services, e-commerce, and providers competing for information-driven queries. Where the audience doesn't research via AI, GEO is not a sensible investment.",
  },
  {
    q: "How is GEO success measured?",
    a: "By share of answer: the share with which a brand is named in AI answers to defined, commercially relevant questions, relative to competitors and over time. Not by classic rankings or click counts.",
  },
  {
    q: "Does GEO replace classic SEO?",
    a: "No. GEO builds on SEO. Without a clean technical foundation, structured data and authority, durable AI visibility isn't possible. The two disciplines work together, not against each other.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const steps = [
  {
    num: "01",
    title: "Baseline: where does AI name you, where the competition?",
    body: "We measure the starting point. For which commercially relevant questions does your brand currently appear in ChatGPT, Perplexity, Gemini and Google's AI Overviews — and at what share relative to competitors. Only this share-of-answer baseline shows where you stand and where the gaps are.",
  },
  {
    num: "02",
    title: "Commercial prioritisation, not scattergun",
    body: "Not all AI visibility is valuable. We prioritise question clusters by commercial relevance: where do users prepare purchase or engagement decisions, and where is the AI answer the moment the choice is made. Topics without commercial impact, we deliberately leave out.",
  },
  {
    num: "03",
    title: "Entities, structure and citability",
    body: "AI systems cite sources they can clearly understand and place. We sharpen your entity — consistent naming across all channels, structured data per Schema.org, verifiable facts, author and source attribution — and build content so it's extractable as clearly delineated answer blocks. Plus the technical side: crawlability for AI bots, clean markup, llms.txt.",
  },
  {
    num: "04",
    title: "Measurement and iteration, without guarantees",
    body: "AI answers change constantly. We track your share of answer over time, keep content current and adjust. What we report is your real share of the relevant answers and how it develops — not a placement we couldn't guarantee in the first place.",
  },
];

const capabilities = [
  { name: "Share-of-answer measurement", detail: "ChatGPT, Perplexity, Gemini, AI Overviews" },
  { name: "Entity modelling", detail: "consistent brand and topic entities across the open web" },
  { name: "Structured data", detail: "Schema.org, FAQ, verifiable facts" },
  { name: "Answer & snippet optimisation", detail: "extractable content blocks" },
  { name: "Technical AI crawlability", detail: "bot access, llms.txt, clean markup" },
  { name: "E-E-A-T signals", detail: "author profiles, sources, demonstrable expertise" },
  { name: "Digital PR & third-party mentions", detail: "visibility where your category is discussed" },
  { name: "Integration with SEO", detail: "shared technical foundation" },
];

const stack = [
  { name: "Google Search Console", small: "indexing, crawlability" },
  { name: "Sistrix", small: "visibility, competition" },
  { name: "Screaming Frog", small: "technical audits, structured data" },
];

export const Route = createFileRoute("/en/services/geo-generative-engine-optimization")({
  head: () => {
    const h = pageHead("geoGenerativeEngine", "en");
    const meta = [...h.meta, { name: "robots", content: "index, follow, max-image-preview:large" }];
    const scripts = [
      jsonLdScript(
        serviceSchema({
          name: "Generative Engine Optimization (GEO)",
          description: HERO_DESCRIPTION,
          url: PAGE_PATH,
          serviceType: "Generative Engine Optimization",
        }),
      ),
      jsonLdScript(
        breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "GEO — Generative Engine Optimization", path: PAGE_PATH },
        ]),
      ),
      jsonLdScript(faqJsonLd),
    ];
    return { meta, links: h.links, scripts };
  },
  component: GeoPage,
});

function GeoPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="GEO — Generative Engine Optimization"
          headline={
            <>
              GEO isn't a hype discipline — it's whether AI systems <em>name you as a source.</em>
            </>
          }
          lead="Classic rankings now answer only half the question. More and more purchase and vendor research ends inside ChatGPT, Perplexity and Google's AI Overviews — in a synthesised answer that names a handful of sources and leaves out everything else. Generative Engine Optimization makes sure you're among those sources. Not everywhere — where the mention actually pays off."
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "GEO — Generative Engine Optimization" },
          ]}
        />

        <Section
          number="02"
          kicker="Position"
          headline={
            <>
              Being found and being recommended are <em>two different things.</em>
            </>
          }
        >
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">
                Classic SEO gets you into the results list. GEO decides whether an AI names you in its answer — and
                that follows different rules. An AI doesn't rank ten blue links; it builds an answer from a few
                sources it considers trustworthy. If you're not among them, you're not on page two. You're invisible.
              </p>
              <p className="mb-5">
                The market has responded to this shift with promises that don't hold up to scrutiny. Guaranteed
                placements in AI answers don't exist: which sources a system cites changes from month to month and
                from prompt to prompt. Anyone selling guarantees is selling an illusion.
              </p>
              <p>
                Our position is the same as it is for everything we do. GEO is not an end in itself. Not every industry
                and not every question runs through AI systems — and where it doesn't, GEO is wasted budget. We first
                establish whether and where AI visibility creates real commercial leverage for your business, and we
                invest only there. We make the outcome measurable: your share of the AI answers to commercially
                relevant questions — not a metric that looks good and means nothing.
              </p>
            </div>
          </RevealOnScroll>
          <Callout>
            Visibility in AI answers is only valuable when the question behind it leads to your business.
          </Callout>
        </Section>

        <Section
          variant="tinted"
          number="03"
          kicker="Approach"
          headline={
            <>
              Four phases that make AI visibility <em>steerable.</em>
            </>
          }
        >
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-12 rounded-lg bg-white p-6 md:p-10">
              <div className="grid gap-8 md:grid-cols-[minmax(0,_2fr)_minmax(0,_3fr)] md:gap-14">
                <div>
                  <p className="eyebrow mb-3">Optional building block</p>
                  <h3 className="font-display text-xl text-ink">
                    Being recommended means being named elsewhere
                  </h3>
                </div>
                <p className="text-[17px] leading-relaxed text-ink-2">
                  LLMs don't draw their answers from your website alone. They learn from what third parties write
                  about you: trade media, industry directories, comparison portals, community platforms. A substantial
                  part of GEO therefore happens off your own pages — consistent entity signals across the open web,
                  targeted digital PR, and presence where your category is discussed. This work is slower than on-page
                  optimisation, but it decides whether an AI names you on its own.
                </p>
              </div>
            </div>
          </RevealOnScroll>
          <Callout>
            GEO doesn't replace SEO. It builds on the same technical foundation — and goes one step further.
          </Callout>
        </Section>

        <Section
          number="04"
          kicker="Capabilities & Stack"
          headline={
            <>
              What makes AI visibility <em>steerable.</em>
            </>
          }
        >
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <RelatedServices
          locale="en"
          fromId="geo-generative-engine-optimization"
          number="06"
          items={relatedBySource["geo-generative-engine-optimization"]}
        />

        <Section number="07" kicker="FAQ" headline={<>Frequently asked questions about <em>GEO.</em></>}>
          <RevealOnScroll>
            <dl className="divide-y divide-rule rounded-lg border border-rule bg-white">
              {FAQS.map((f) => (
                <div key={f.q} className="px-6 py-6 md:px-8 md:py-7">
                  <dt className="font-display text-lg text-ink md:text-xl">{f.q}</dt>
                  <dd className="mt-3 text-[15px] leading-relaxed text-ink-3 md:text-base">{f.a}</dd>
                </div>
              ))}
            </dl>
          </RevealOnScroll>
        </Section>

        <Section kicker="Engagement">
          <CTABlock
            headline={
              <>
                Get <em>named</em> in AI answers — where it counts.
              </>
            }
            body="Want to know whether and where AI systems already recommend you — and which topics create the leverage?"
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>

        <GlossaryTerms
          locale="en"
          ids={["geo", "ai-overviews", "ai-mode", "snippet-suitability", "eeat", "pillar-content"]}
        />
      </main>
      <Footer />
    </>
  );
}
